import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { roleLabel } from '../../utils/roleLabel';
import { canCreateJobCards } from '../../utils/permissions';
import {
  FiMenu, FiGlobe, FiRepeat, FiCamera, FiSearch, FiChevronDown, FiLogOut, FiPlus,
} from 'react-icons/fi';
import NotificationDropdown from '../NotificationDropdown';
import BranchSelector from '../BranchSelector';
import HelpButton from './HelpButton';
import SupportButton from './SupportButton';
import ThemeToggle from '../ui/ThemeToggle';
import Button from '../ui/Button';
import { LangToggle } from '../staff';

const Header = ({
  user, viewMode, isStaffExperience, title, onToggleSidebar, onToggleLanguage, onViewModeSwitch, onScan, onOpenSearch, onLogout,
}) => {
  const { t, i18n } = useTranslation();
  const roleName = user?.role?.name;
  // Any non-staff role can preview the staff experience. Staff are already in it,
  // and Super Admin routing never renders the staff view (it short-circuits to the
  // Super Admin dashboard), so the switch would be a no-op for them.
  const canSwitchView = !!roleName && roleName !== 'Staff' && roleName !== 'Super Admin';
  // "New job card" is the platform's most-used action (owner, 2026-10-09), so it
  // sits in the header on every page. Super Admin passes every permission check
  // but runs no workshop, and the staff experience has its own job list.
  const navigate = useNavigate();
  const canNewJobCard =
    !isStaffExperience && roleName !== 'Super Admin' && canCreateJobCards(user);

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Close the account menu on outside click or Escape.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onPointerDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  return (
    <header className="sticky top-0 z-30 h-[calc(4rem_+_env(safe-area-inset-top))] pt-[env(safe-area-inset-top)] bg-surface/85 backdrop-blur border-b border-edge">
      <div className="flex items-center justify-between gap-2 px-3 sm:px-6 h-full">
        <div className="flex items-center gap-1.5 sm:gap-3 min-w-0 flex-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggleSidebar}
            className="xl:hidden -ms-1 active:bg-surface-3 shrink-0"
            aria-label={t('cm.header.menu')}
          >
            <FiMenu size={20} />
          </Button>
          <h2 className="text-base sm:text-lg font-semibold text-content truncate min-w-0">{title}</h2>
        </div>

        {/* Global search — opens the command palette */}
        <button
          onClick={onOpenSearch}
          className="hidden md:flex items-center gap-2.5 flex-1 max-w-md mx-4 h-10 px-3.5 rounded-xl bg-surface-2/70 border border-edge text-content-muted hover:border-edge-strong hover:text-content-secondary transition-colors text-sm"
        >
          <FiSearch size={15} />
          <span className="truncate">{t('commandPlaceholder')}</span>
          <kbd className="ms-auto hidden lg:inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded border border-edge bg-surface text-[10px] font-medium text-content-muted">
            Ctrl K
          </kbd>
        </button>

        <div className="flex items-center gap-1 sm:gap-1.5 shrink-0">
          {/* Mobile search icon */}
          <Button
            variant="ghost"
            size="icon"
            onClick={onOpenSearch}
            className="md:hidden"
            aria-label={t('commandPlaceholder')}
          >
            <FiSearch size={18} />
          </Button>
          {/* Branch picker is tenant-scoped. Super Admin is platform-wide and
              never operates inside a single branch, so it would be meaningless
              (and confusing) for them — hide it. */}
          {/* Help moves into the account menu on phones — seven inline controls
              overflowed the 390px bar and clipped the avatar off-screen. */}
          {roleName !== 'Super Admin' && (
            <span className="hidden sm:block">
              <HelpButton />
            </span>
          )}
          {/* Support is one tap from anywhere, next to help. Renders nothing at
              all when the server has no support chat configured, so it costs no
              width on a deployment that does not use it. */}
          {roleName !== 'Super Admin' && (
            <span className="hidden sm:block">
              <SupportButton />
            </span>
          )}
          {roleName !== 'Super Admin' && <BranchSelector />}

          {/* Language — staff get the big one-tap LangToggle; admin keeps the
              compact globe button. */}
          {isStaffExperience ? (
            <LangToggle />
          ) : (
            <button
              onClick={onToggleLanguage}
              className="hidden sm:flex items-center gap-1.5 px-2.5 py-2.5 text-content-secondary hover:text-content hover:bg-surface-2 rounded-lg transition-colors text-sm font-medium"
              title={i18n.language === 'en' ? t('cm.header.switchToArabic') : t('cm.header.switchToEnglish')}
            >
              <FiGlobe size={17} />
              <span className="hidden lg:inline">{i18n.language === 'en' ? 'العربية' : 'EN'}</span>
            </button>
          )}

          {/* Theme lives inline on tablet/desktop; on mobile it moves into the
              account menu to keep the top bar uncrowded. */}
          <span className="hidden sm:block">
            <ThemeToggle />
          </span>

          <NotificationDropdown />

          {/* New job card — opens the same job-type step the phone + menu and
              the customer/vehicle profiles use (JobCards reads quickCreate). */}
          {/* Tablet and up only: on a phone the right-hand cluster has no room
              for one more control (the avatar would clip off a 375px screen
              for a multi-branch tenant), and MobileBottomNav's + already opens
              this same flow there. */}
          {canNewJobCard && (
            <span className="hidden md:inline-flex">
              <Button
                variant="primary"
                size="md"
                icon={FiPlus}
                onClick={() => navigate('/job-cards', { state: { quickCreate: true } })}
                className="ms-1"
              >
                {t('qc.newJobCard')}
              </Button>
            </span>
          )}

          {/* QR Scanner — outlined when New job card is the primary action. */}
          <Button
            variant={canNewJobCard ? 'outline' : 'primary'}
            size="md"
            icon={FiCamera}
            onClick={onScan}
            aria-label={t('scanQR')}
            className="ms-1"
          >
            <span className="hidden md:inline">{t('scanQR')}</span>
          </Button>

          {/* Account menu — avatar opens view-switch + logout (and, on mobile,
              theme + language which are hidden from the top bar). */}
          <div className="relative ps-1 sm:ps-2 ms-0.5 sm:ms-1 sm:border-s border-edge shrink-0" ref={menuRef}>
            <button
              onClick={() => setMenuOpen((v) => !v)}
              className="flex items-center gap-1 sm:gap-2.5 rounded-lg px-0.5 sm:px-1.5 py-1 hover:bg-surface-2 transition-colors"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              aria-label={t('cm.header.account', 'Account menu')}
            >
              <div className="text-end hidden lg:block">
                <p className="text-[13px] font-semibold text-content leading-tight">
                  {user?.first_name} {user?.last_name}
                </p>
                <p className="text-[11px] text-content-muted leading-tight">{roleLabel(user?.role)}</p>
              </div>
              <div className="w-9 h-9 rounded-full bg-content text-content-inverse flex items-center justify-center font-semibold text-xs">
                {user?.first_name?.[0]}
                {user?.last_name?.[0]}
              </div>
              <FiChevronDown
                size={15}
                className={`hidden sm:block text-content-muted transition-transform ${menuOpen ? 'rotate-180' : ''}`}
              />
            </button>

            {menuOpen && (
              <div
                role="menu"
                className="absolute end-0 mt-2 w-60 rounded-xl border border-edge bg-surface shadow-lg py-1.5 z-50"
              >
                {/* Identity — useful on widths where the inline name is hidden */}
                <div className="px-3.5 py-2.5 border-b border-edge lg:hidden">
                  <p className="text-sm font-semibold text-content leading-tight truncate">
                    {user?.first_name} {user?.last_name}
                  </p>
                  <p className="text-[11px] text-content-muted leading-tight">{roleLabel(user?.role)}</p>
                </div>

                {/* Mobile-only: theme + language, which are hidden from the
                    crowded mobile top bar, are reachable here instead. */}
                <div className="sm:hidden border-b border-edge py-1">
                  {/* Help lives here on phones (hidden from the top bar above)
                      so the manual/ask-AI drawer is never out of reach. */}
                  {roleName !== 'Super Admin' && (
                    <div className="flex items-center justify-between px-3.5 py-2">
                      <span className="text-sm font-medium text-content-secondary">{t('cm.header.help')}</span>
                      <HelpButton />
                    </div>
                  )}
                  {/* Support follows help down here on phones — the top bar
                      already clipped the avatar at 390px with seven controls.
                      The label row is drawn by SupportButton itself: whether
                      support exists is subscribed state, and reading it here
                      would not re-render this menu when it arrives. */}
                  {roleName !== 'Super Admin' && <SupportButton showLabel />}
                  <button
                    role="menuitem"
                    onClick={() => { setMenuOpen(false); onToggleLanguage(); }}
                    className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm font-medium text-content-secondary hover:bg-surface-2 hover:text-content transition-colors"
                  >
                    <FiGlobe size={16} />
                    <span>{i18n.language === 'en' ? 'العربية' : 'English'}</span>
                  </button>
                  <div className="flex items-center justify-between px-3.5 py-2">
                    <span className="text-sm font-medium text-content-secondary">
                      {t('cm.header.theme', 'Theme')}
                    </span>
                    <ThemeToggle />
                  </div>
                </div>

                {canSwitchView && (
                  <button
                    role="menuitem"
                    onClick={() => { setMenuOpen(false); onViewModeSwitch(); }}
                    className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm font-medium text-content-secondary hover:bg-surface-2 hover:text-content transition-colors"
                  >
                    <FiRepeat size={16} />
                    <span>
                      {viewMode === 'admin'
                        ? t('cm.header.switchToStaffView')
                        : t('cm.header.switchToAdminView')}
                    </span>
                  </button>
                )}

                <button
                  role="menuitem"
                  onClick={() => { setMenuOpen(false); onLogout?.(); }}
                  className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm font-medium text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-500/10 transition-colors"
                >
                  <FiLogOut size={16} />
                  <span>{t('logout')}</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
